import {
  endpoint,
  sourceName,
} from 'shared/logger/config/sumoLogicBaseConfig';

/** Logs kept while Sumo cannot be reached, sent once it can; the oldest are dropped beyond this. */
export const MAX_KEPT_LOGS = 200;

interface SumoLogSenderOptions {
  send: (body: object) => Promise<void>;
  isOnline: () => boolean;
}

export interface SumoLogSender {
  log: (body: object) => void;
  /** Sends the kept logs, oldest first; stops at the first that fails. */
  sendKeptLogs: () => Promise<void>;
}

/**
 * Sends the worker's logs to Sumo, in the order they were made. Without network
 * a log is not lost: it is kept in memory (until the worker stops) and sent when
 * the network is back (`sendKeptLogs` on the worker's `online`) or the next log
 * gets through. A failed send is told in the console once, not for every log.
 */
export function createSumoLogSender({ send, isOnline }: SumoLogSenderOptions): SumoLogSender {
  const keptLogs: object[] = [];
  let isUnreachable = false;
  let isSendingKeptLogs = false;

  const keep = (body: object): void => {
    keptLogs.push(body);
    if (keptLogs.length > MAX_KEPT_LOGS) {
      keptLogs.shift();
    }
  };

  // By reference: logs kept while it was sent may have dropped the oldest ones.
  const removeSent = (body: object): void => {
    const index = keptLogs.indexOf(body);
    if (index !== -1) {
      keptLogs.splice(index, 1);
    }
  };

  const markUnreachable = (error: unknown): void => {
    if (!isUnreachable) {
      // eslint-disable-next-line no-console
      console.log('could not log to sumologic, the logs are kept until it can be reached', error);
    }
    isUnreachable = true;
  };

  const sendKeptLogs = async (): Promise<void> => {
    if (isSendingKeptLogs) {
      return;
    }
    isSendingKeptLogs = true;
    try {
      while (keptLogs.length > 0) {
        const [oldest] = keptLogs;
        await send(oldest);
        removeSent(oldest);
      }
      isUnreachable = false;
    } catch (error) {
      markUnreachable(error);
    } finally {
      isSendingKeptLogs = false;
    }
  };

  const log = (body: object): void => {
    // Behind logs still waiting, a new one waits its turn.
    if (!isOnline() || keptLogs.length > 0) {
      keep(body);
      if (isOnline()) {
        void sendKeptLogs();
      }
      return;
    }
    send(body).then(
      () => {
        isUnreachable = false;
      },
      (error: unknown) => {
        keep(body);
        markUnreachable(error);
      }
    );
  };

  return { log, sendKeptLogs };
}

// Sumologger doesn't work in service worker - XHR is not supported
async function postToSumo(body: object): Promise<void> {
  await fetch(endpoint, {
    body: JSON.stringify(body),
    method: 'POST',
    // @ts-ignore specific sumo headers
    headers: {
      'X-Sumo-Name': sourceName,
      'X-Sumo-Category': 'Service worker',
    },
  });
}

export const swSumoLogicLogger = createSumoLogSender({
  send: postToSumo,
  isOnline: () => self.navigator.onLine !== false,
});
